import buildSubjectPath from '@UI/Pages/Beneficiaries/Shared/Helpers/buildSubjectPath';
import type SubjectAddress from '@Domain/Subjects/SubjectAddress';
import FetchWorkflowRun from '@Application/WorkflowRuns/WorkflowRun/Queries/FetchWorkflowRun';
import fetchWorkflowRun from '@Application/WorkflowRuns/WorkflowRun/fetchWorkflowRun';
import { PermissionEnum } from '@Application/Security/Roles';
import EmptyState from '@Guidelines/Component/EmptyState';
import ErrorState from '@Guidelines/Component/ErrorState';
import useBeneficiaryInterviewsList from '@UI/Pages/Beneficiaries/Hooks/useBeneficiaryInterviewsList';
import LoadedPreparation from '@UI/Pages/Beneficiaries/BeneficiaryDetails/Pages/Preparations/Preparation/LoadedPreparation';
import isWorkflowRunNotFoundFailure from '@UI/Pages/Beneficiaries/BeneficiaryDetails/Pages/Shared/Helpers/isWorkflowRunNotFoundFailure';
import useSubjectModifiability from '@UI/Pages/Beneficiaries/BeneficiaryDetails/Pages/Hooks/useSubjectModifiability';
import useHasPermission from '@UI/Providers/PermissionProvider/useHasPermission';
import useStrictCurrentFacility from '@UI/Providers/CurrentFacilityProvider/useStrictCurrentFacility';
import { css } from '@emotion/react';
import { FileQuestion } from 'lucide-react';
import * as React from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useStoreQuery } from '@verclam/synapse';
import { Button, Skeleton } from '@verclam/ui-kit';

const skeletonStackStyle = css`
	display: flex;
	flex-direction: column;
	gap: 0.75rem;
	max-width: 48.75rem;
`;

type Props = {
	readonly subject: SubjectAddress;
	readonly preparationId: number;
};

/**
 * Owns the loading, not-found, error and nominal branches of the preparation screen, and the reads it
 * needs itself: the preparation (kept current by the dossier event stream), the
 * dossier (archived?) and its interviews merged with this device's recordings. The documents and
 * imported documents that resolve element titles are read by the components that draw them.
 */
const PreparationContent: React.FC<Props> = ({ subject, preparationId }) => {
	const dispatch = useDispatch();
	const navigate = useNavigate();
	const { currentFacility } = useStrictCurrentFacility();
	const facilityId = currentFacility.id;
	const hasWritePermission = useHasPermission(
		[PermissionEnum.INTERVIEW_WRITE, PermissionEnum.DOCUMENT_GENERATION_LAUNCH],
		'any',
	);
	const hasLaunchPermission = useHasPermission(PermissionEnum.DOCUMENT_GENERATION_LAUNCH);
	const [isNotFound, setIsNotFound] = React.useState(false);

	const {
		data: preparation,
		error: preparationError,
		refetch: refetchWorkflowRun,
	} = useStoreQuery({
		selector: (state) => state.workflowRunsStore.workflowRun,
		stopOn: (value) => value !== null && value.id === preparationId,
		query: () =>
			dispatch(fetchWorkflowRun(new FetchWorkflowRun(facilityId, subject, preparationId))).catch(
				(rejection: unknown) => {
					if (isWorkflowRunNotFoundFailure(rejection)) {
						setIsNotFound(true);
					}

					throw rejection;
				},
			),
		deps: [facilityId, subject, preparationId],
	});

	const { isKnown, canModify, isArchived } = useSubjectModifiability(subject);

	const interviewsList = useBeneficiaryInterviewsList(subject);

	// A stale detail of another preparation must never be mistaken for this one.
	const current = preparation !== null && preparation.id === preparationId ? preparation : null;

	const { refetch: refetchInterviews } = interviewsList;
	const handleRefresh = React.useCallback(() => {
		refetchWorkflowRun();
		refetchInterviews();
	}, [refetchWorkflowRun, refetchInterviews]);

	// A source whose interview the list does not hold yet (recorded elsewhere, just confirmed) reloads
	// the list once; an interview already asked for is never asked for again.
	const requestedInterviewIdsRef = React.useRef<ReadonlySet<number>>(new Set());
	const { serverInterviews, isServerDataLoaded } = interviewsList;

	React.useEffect(() => {
		if (current === null || !isServerDataLoaded) {
			return;
		}

		const knownInterviewIds = new Set(serverInterviews.map((interview) => interview.id));
		const missingInterviewIds = current.sources
			.map((source) => source.interviewId)
			.filter(
				(interviewId): interviewId is number =>
					interviewId !== null &&
					!knownInterviewIds.has(interviewId) &&
					!requestedInterviewIdsRef.current.has(interviewId),
			);

		if (missingInterviewIds.length > 0) {
			requestedInterviewIdsRef.current = new Set([
				...requestedInterviewIdsRef.current,
				...missingInterviewIds,
			]);
			refetchInterviews();
		}
	}, [current, isServerDataLoaded, serverInterviews, refetchInterviews]);

	const handleBackToDossier = React.useCallback(() => {
		navigate(buildSubjectPath(subject));
	}, [navigate, subject]);

	if (current === null && isNotFound) {
		return (
			<EmptyState
				title="Cette préparation est introuvable."
				description="Elle a peut-être été abandonnée."
				icon={<FileQuestion size={28} />}
				action={<Button type="button" label="Retour au dossier" onClick={handleBackToDossier} />}
			/>
		);
	}

	// Only a read that has actually failed is an error; a still-absent value is a loading state.
	if (current === null && preparationError !== null) {
		return <ErrorState title="Impossible de charger la préparation." onRetry={refetchWorkflowRun} />;
	}

	if (current === null || !isKnown) {
		return (
			<div css={skeletonStackStyle}>
				<Skeleton height="5rem" animation="wave" shape="rectangle" />
				<Skeleton height="12rem" animation="wave" shape="rectangle" />
				<Skeleton height="10rem" animation="wave" shape="rectangle" />
			</div>
		);
	}

	return (
		<LoadedPreparation
			subject={subject}
			preparation={current}
			canWrite={hasWritePermission && canModify}
			canLaunch={hasLaunchPermission && canModify}
			isArchived={isArchived}
			entries={interviewsList.entries}
			localInterviews={interviewsList.localInterviews}
			onRefresh={handleRefresh}
		/>
	);
};

export default PreparationContent;
